import React, { useContext, useEffect, useState } from 'react'
import { IonContent, IonPage, IonText, useIonViewWillEnter } from '@ionic/react'
import { CustomSpacer, TextStyle, StyleIonHeader, FooterButtons, PagingContent, ParamOption } from '../../../components'
import styled from 'styled-components'
import { StoresContext } from '../../../provider/StoreProvider'
import { RouteComponentProps } from 'react-router'
import moment from 'moment-timezone'
import { observer } from 'mobx-react'
import { onParams, PARAM_FILTER, PARAM_SORT } from '../../../common'

export const Account: React.FC<RouteComponentProps> = observer(() => {
  const { commonStore, myStore } = useContext(StoresContext)
  const pagingStore = myStore.accountPagingStore
  const [sortType, setSortType] = useState<PARAM_SORT>(PARAM_SORT.LATEST)
  const [filterType, setFilterType] = useState(PARAM_FILTER.ALL)

  useIonViewWillEnter(() => {
    commonStore.setShowTab(false)
  })

  useEffect(() => {
    pagingStore.load(undefined, true)
  }, [])

  useEffect(() => {
    onParams({
      store: pagingStore,
      sortType: sortType,
      filterType: filterType,
      filterName: 'type',
    })
  }, [sortType, filterType])

  const filterList = [
    {
      type: PARAM_FILTER.ALL,
      title: '전체',
    },
    {
      type: PARAM_FILTER.SALE,
      title: '조각구매',
    },
    {
      type: PARAM_FILTER.SELL,
      title: '트레이드(매도)',
    },
    {
      type: PARAM_FILTER.BUY,
      title: '트레이드(매수)',
    },
    {
      type: PARAM_FILTER.DEPOSIT,
      title: '예치금 충전',
    },
    {
      type: PARAM_FILTER.WITHDRAWAL,
      title: '예치금 출금',
    },
    {
      type: PARAM_FILTER.REVENUE,
      title: '매각 대금',
    },
    {
      type: PARAM_FILTER.REFUND,
      title: '환불',
    },
  ]

  const sortList = [
    {
      type: 'LATEST',
      title: '최근 거래순',
    },
    {
      type: 'END_DATE',
      title: '오래된 거래순',
    },
  ]

  return (
    <IonPage>
      <StyleIonHeader type={'SUB'} title={'입출금 내역'} href={'/myPage'} />
      <IonContent>
        <ParamOption
          slideFilterData={{
            filterList: filterList,
            filterType: filterType,
            setFilterType: setFilterType,
          }}
          sortData={{
            sortList: sortList,
            setSortType: setSortType,
          }}
        />
        <PagingContent
          emptyText="조회 가능한 내역이 없습니다."
          pagingStore={pagingStore}
          loadParam={{ data: { type: filterType } }}
        >
          {pagingStore.list.map((d, i) => (
            <HistoryContainer key={i}>
              <DateContainer>
                <TextStyle type={'b_12'}>{`${moment(new Date(d.createdDate)).format('YY.MM.DD')}`}</TextStyle>
                <TextStyle type={'r_12'}>{`${moment(new Date(d.createdDate)).format('HH:MM')}`}</TextStyle>
              </DateContainer>
              <SectionContainer>
                {d.type === 'SALE' && (
                  <>
                    <TextStyle type={'b_14'}>조각 구매</TextStyle>
                    <ContentWrap>
                      <TextStyle type={'r_12'}>{`주문번호: ${d.purchase?.orderSequence ?? '-'}`}</TextStyle>
                      <TextStyle type={'b_16'} color={'secondary_main'}>
                        {`-${(d.purchase.cost * Math.abs(d.purchase.count)).toLocaleString() ?? 0} 원`}
                      </TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'r_13'}>{`${d.purchase?.balance.toLocaleString() ?? 0} 원`}</TextStyle>
                    </ContentWrap>
                    <CustomSpacer spacing="16px" />
                  </>
                )}
                {d.type === 'BUY' && (
                  <>
                    <ContentWrap>
                      <TextStyle type={'b_14'}>트레이드(매수)</TextStyle>
                      <TextStyle type={'r_12'}>{`(수수료 포함)`}</TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <TextStyle type={'r_12'}>{`주문번호: ${d.purchase?.orderSequence ?? '-'}`}</TextStyle>
                      <TextStyle type={'b_16'} color={'secondary_main'}>
                        {`-${(d.purchase.cost * Math.abs(d.purchase.count) + d.purchase.fee).toLocaleString() ?? 0} 원`}
                      </TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'r_13'}>{`${d.purchase?.balance.toLocaleString() ?? 0} 원`}</TextStyle>
                    </ContentWrap>
                    <CustomSpacer spacing="16px" />
                  </>
                )}
                {d.type === 'SELL' && (
                  <>
                    <ContentWrap>
                      <TextStyle type={'b_14'}>트레이드(매도)</TextStyle>
                      <TextStyle type={'r_12'}>{`(수수료 포함)`}</TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <TextStyle type={'r_12'}>{`주문번호: ${d.purchase?.orderSequence ?? '-'}`}</TextStyle>
                      <TextStyle type={'b_16'} color={'error_main'}>
                        {`+${(d.purchase.cost * Math.abs(d.purchase.count) - d.purchase.fee).toLocaleString() ?? 0} 원`}
                      </TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'r_13'}>{`${d.purchase?.balance.toLocaleString() ?? 0} 원`}</TextStyle>
                    </ContentWrap>
                    <CustomSpacer spacing="16px" />
                  </>
                )}
                {d.type === 'DEPOSIT' && (
                  <>
                    <TextStyle type={'b_14'}>예치금 충전</TextStyle>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'b_16'} color={'error_main'}>
                        {`+${d.cashDeposit.amount.toLocaleString() ?? 0} 원`}
                      </TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'r_13'}>{`${d.cashDeposit.balance.toLocaleString() ?? 0} 원`}</TextStyle>
                    </ContentWrap>
                    <CustomSpacer spacing="16px" />
                  </>
                )}
                {d.type === 'WITHDRAWAL' && (
                  <>
                    <ContentWrap>
                      <TextStyle type={'b_14'}>예치금 출금</TextStyle>
                      <TextStyle type={'r_12'}>{`(수수료 포함)`}</TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'b_16'} color={'secondary_main'}>
                        {`-${d.cashWithdrawal.amount.toLocaleString() ?? 0} 원`}
                      </TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'r_13'}>{`${d.cashWithdrawal.balance.toLocaleString() ?? 0} 원`}</TextStyle>
                    </ContentWrap>
                    <CustomSpacer spacing="16px" />
                  </>
                )}
                {d.type === 'REVENUE' && (
                  <>
                    <TextStyle type={'b_14'}>매각 대금</TextStyle>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'b_16'} color={'error_main'}>
                        {`+${d.cashRevenue.amount.toLocaleString() ?? 0} 원`}
                      </TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'r_13'}>{`${d.cashRevenue.balance.toLocaleString() ?? 0} 원`}</TextStyle>
                    </ContentWrap>
                    <CustomSpacer spacing="16px" />
                  </>
                )}
                {d.type === 'REFUND' && (
                  <>
                    <TextStyle type={'b_14'}>환불</TextStyle>
                    <ContentWrap>
                      <TextStyle type={'r_12'}>{`주문번호: ${d.purchase?.orderSequence ?? '-'}`}</TextStyle>
                      <TextStyle type={'b_16'} color={'error_main'}>
                        {`+${(d.purchase.cost * Math.abs(d.purchase.count)).toLocaleString() ?? 0} 원`}
                      </TextStyle>
                    </ContentWrap>
                    <ContentWrap>
                      <IonText />
                      <TextStyle type={'r_13'}>{`${d.purchase?.balance.toLocaleString() ?? 0} 원`}</TextStyle>
                    </ContentWrap>
                    <CustomSpacer spacing="16px" />
                  </>
                )}
              </SectionContainer>
            </HistoryContainer>
          ))}
        </PagingContent>
      </IonContent>
      <FooterButtons
        button={{ text: '출금', color: 'primary', link: '/myPage/account/withdrawal' }}
        rightButton={{ text: '충전', color: 'dark', link: '/myPage/account/deposit' }}
      />
    </IonPage>
  )
})

const HistoryContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 5fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
  padding: 16px 20px 0 16px;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: solid 1px var(--ion-color-gray100);
  padding-top: 2px;
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ContentWrap = styled.div`
  display: flex;
  justify-content: space-between;
`
